import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
    IconChevronSprite,
    Polyline1,
    Polyline2,
    Polyline3,
    Polyline4,
    Polyline5
} from '$partials/icons/index.js';
import { Button } from '$partials/ui';
import { useLocale } from '$contexts/locale.jsx';
import Image from '$partials/media/image';
import BackgroundVideo from '$partials/media/background-video';
gsap.registerPlugin(ScrollTrigger);

/**
 * @param {any} richText
 * @returns {React.ReactNode}
 */
const renderHeroText = (richText) => {
    if (!richText?.json?.content) {
        return null;
    }

    return richText.json.content
        .filter((/** @type {any} */ block) => block.content?.length)
        .map((/** @type {any} */ block, /** @type {number} */ i) => (
            <div
                className='animated-statement__lines'
                key={i}>
                {block.content.map((/** @type {any} */ node, /** @type {number} */ j) => {
                    if (node.nodeType !== 'text') {
                        return null;
                    }
                    const isBold = node.marks?.some((/** @type {any} */ mark) => mark.type === 'bold');
                    return isBold ? (
                        <span
                            className='animated-statement__highlight animated-statement__highlight--orange'
                            key={j}>
                            {node.value}
                        </span>
                    ) : (
                        <React.Fragment key={j}>{node.value}</React.Fragment>
                    );
                })}
            </div>
        ));
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleStatement}
 */

const LandingPageHero = ({
    bodyCopy = '',
    cta1Link,
    cta1Label,
    cta2Link,
    cta2Label,
    eyebrow = '',
    heroText,
    heroMedia,
    heroSimple,
    pageTitle,
    polyline,
    theme = ''
}) => {

    const polyLineIcons = [Polyline1, Polyline2, Polyline3, Polyline4, Polyline5];


    const selectedPolyline = polyline === 'None' ? null : polyLineIcons[Number(polyline) - 1] || null;


    const {
        formatURI,
        translate: t,
    } = useLocale();

    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();
    const mediaRef = useRef();

    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            gsap.set(containerRef.current.querySelectorAll('.animation__mask-slide-in'), { opacity: 0 });
            gsap.set(containerRef.current.querySelectorAll('.animated-statement__text'), { opacity: 1 });

            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create reveal hide of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 2.8,
                    ease: 'power3',
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animated-statement__lines'),
                {
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );
        }
    };

    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    useEffect(() => {
        if (mediaRef.current) {
            gsap.from(mediaRef.current, {
                delay: 0.3,
                duration: 1,
                ease: 'power4.out',
                y: '100vh',
            });
        }
    }, []);



    // render
    return (
        <div className='relative overflow-hidden'>
            <section
                className={`overflow-hidden animated-statement relative lg:pt-120 theme-${theme} ${heroSimple ? 'pb-24 md:pb-40 pt-80 md:pt-120' : 'bg-theme-themed-surface-bg pb-16 sm:pb-40 md:pb-80 pt-72 sm:pt-80 md:pt-120 '} rounded`}
                ref={containerRef}>
                <div className='relative z-10 layout-container layout-site-outer-padding w-full overflow-hidden'>
                    <div className='col-wrapper flex flex-col gap-y-24 sm:flex-row'>
                        <div className={`col-padding flex flex-col ${!heroSimple ? 'sm:w-6/12 md:pt-130' : 'sm:w-8/12 md:w-5/12'}`}>
                            {/* eyebrow */}
                            {eyebrow && (
                                <p className={`${!heroSimple ? 'animation__mask-slide-in' : ''} type-style-eyebrow text-text-accent mb-24`}>
                                    {eyebrow}
                                </p>
                            )}

                            {/* hero text */}
                            {heroText && !heroSimple &&(
                                <h1 className='type-style-1-extended w-full break-words text-theme-themed-text-accent animated-statement__text opacity-0'>
                                    {!heroSimple ? renderHeroText(heroText) : heroText.json.content.map((block) => block.content.map((node) => node.value).join('')).join('')}
                                </h1>
                            )}

                            {/* hero text */}
                            {heroSimple && pageTitle && (
                                <h1 className='type-style-1-extended text-text-primary'>
                                    {pageTitle}
                                </h1>
                            )}

                            {/* body copy */}
                            {bodyCopy && (
                                <p className={`text-text-secondary type-style-body ${heroSimple ? 'mt-20' : 'mt-24 animation__mask-slide-in '}`}>
                                    {bodyCopy}
                                </p>
                            )}

                            {/* cta buttons */}
                            {(cta1Link || cta2Link) && (
                                <div className={` ${heroSimple ? 'mt-20' : 'animation__mask-slide-in mt-24'} flex flex-wrap gap-8`}>
                                    {cta1Link && (
                                        <Button
                                            as='a'
                                            className='button button--primary'
                                            href={formatURI(cta1Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta1Label}</span>
                                            </span>
                                            <span className='button__icon button__icon--animate'>
                                                <IconChevronSprite />
                                            </span>
                                        </Button>
                                    )}
                                    {cta2Link && (
                                        <Button
                                            as='a'
                                            className='button button--secondary'
                                            href={formatURI(cta2Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta2Label}</span>
                                            </span>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                        {heroMedia?.src && !heroSimple && (
                            heroMedia?.contentType === 'video/mp4' ? (
                                <>
                                    {/* spacer */}
                                    <div className='hidden lg:block w-1/12 col-padding' />
                                    {/* image */}
                                    <div className='sm:w-6/12 lg:w-5/12 col-padding'>
                                        <figure className='rounded overflow-hidden pointer-events-none select-none aspect-square h-full'>
                                            <BackgroundVideo
                                                className='rounded object-cover h-full'
                                                containerClassName='w-full rounded h-full'
                                                src={heroMedia?.src}/>
                                        </figure>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* spacer */}
                                    <div className='hidden lg:block w-1/12 col-padding' />

                                    {/* image */}
                                    <div className='sm:w-6/12 lg:w-5/12 col-padding'>
                                        <figure className='rounded overflow-hidden pointer-events-none select-none aspect-square h-full'>
                                            <Image
                                                className='h-full object-cover'
                                                containerClassName='h-full object-cover'
                                                height={'100%'}
                                                loadWidth={840}
                                                src={heroMedia?.src}
                                                width={'100%'} />
                                        </figure>
                                    </div>
                                </>
                            )
                        )}
                    </div>

                </div>
                {/* polyline */}
                {selectedPolyline && !heroSimple && (
                    <div
                        aria-hidden='true'
                        className='absolute top-0 right-0 w-[50dvw] z-[1]'>
                        {React.createElement(selectedPolyline, {
                            className: 'w-full h-auto text-theme-themed-polyline'
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    alignment: PropTypes.string,
    bodyCopy: PropTypes.string,
    ctaButton1: PropTypes.object,
    ctaButton2: PropTypes.object,
    eyebrow: PropTypes.string,
    heroText: PropTypes.object,
    polyline: PropTypes.string,
    theme: PropTypes.string
};

LandingPageHero.propTypes = propTypes;

export default LandingPageHero;
